"use client";

import { CommandItem } from "@universe/ui/components/command";
import { useIsMobile } from "@universe/ui/hooks/use-mobile";
import { cn } from "@universe/ui/lib/utils";
import { Check } from "lucide-react";

import CurrencyFlag from "../currency-flag";

type AccountSelectItemProps = {
  value: string;
  label: string;
  name: string;
  searchIndex: string;
  isSelected: boolean;
  onSelect: () => void;
  disabled?: boolean;
  disabledReason?: string;
};

/**
 * Shared account select item component.
 * Uses consistent font weights: label is font-medium, name is text-muted-foreground.
 */
export function AccountSelectItem({
  value,
  label,
  name,
  searchIndex,
  isSelected,
  onSelect,
  disabled = false,
  disabledReason,
}: AccountSelectItemProps) {
  const isMobile = useIsMobile();

  return (
    <CommandItem
      className={cn(
        "mb-1 lg:mb-2 !py-1.5 data-[selected=true]:bg-brand-primary/5",
        disabled ? "hover:cursor-not-allowed" : "hover:cursor-pointer"
      )}
      key={searchIndex}
      value={searchIndex}
      onSelect={() => !disabled && onSelect()}
      disabled={disabled}
    >
      <div className="flex items-center gap-4 px-2">
        <CurrencyFlag
          currency={value}
          size={isMobile ? "28px" : "32px"}
          className={cn(disabled ? "opacity-50" : "opacity-100")}
        />
        <div className="flex flex-col flex-1">
          <div className="flex items-center gap-2">
            <p
              className={cn(
                "text-sm md:text-base font-medium",
                disabled ? "opacity-50" : "opacity-100"
              )}
            >
              {label}
            </p>
            {disabled && disabledReason && (
              <span className="px-2 py-0.5 text-xs font-medium text-gray-800 bg-brand-primary/10 rounded-full">
                {disabledReason}
              </span>
            )}
          </div>
          <p
            className={cn(
              "text-xs md:text-sm font-medium text-muted-foreground",
              disabled ? "opacity-60" : "opacity-100"
            )}
          >
            {name}
          </p>
        </div>
      </div>
      {!disabled && (
        <Check
          className={cn("ml-auto", isSelected ? "opacity-100" : "opacity-0")}
        />
      )}
    </CommandItem>
  );
}
